import { createReducer } from '@reduxjs/toolkit'

import { updateVersion } from '../global/actions'
import {
  addTransaction,
  checkedTransaction,
  clearAllTransactions,
  finalizeTransaction,
  SerializableTransactionReceipt,
  TransactionInfo,
} from './actions'

const now = () => new Date().getTime()

export interface TransactionDetails {
  hash: string
  receipt?: SerializableTransactionReceipt
  lastCheckedBlockNumber?: number
  addedTime: number
  confirmedTime?: number
  from: string
  info: TransactionInfo
}

export interface TransactionState {
  [networkId: number]: {
    [txHash: string]: TransactionDetails
  }
}

export const initialState: TransactionState = {}

export default createReducer(initialState, (builder) =>
  builder
    .addCase(updateVersion, (transactions) => {
      // in case there are any transactions in the store with the old format, remove them
      Object.keys(transactions).forEach((networkId) => {
        const chainTransactions = transactions[networkId as unknown as number]
        Object.keys(chainTransactions).forEach((hash) => {
          if (!('info' in chainTransactions[hash])) {
            // clear old transactions that don't have the right format
            delete chainTransactions[hash]
          }
        })
      })
    })
    .addCase(addTransaction, (transactions, { payload: { networkId, from, hash, info } }) => {
      if (transactions[networkId]?.[hash]) {
        throw Error('Attempted to add existing transaction.')
      }
      const txs = transactions[networkId] ?? {}
      txs[hash] = { hash, info, from, addedTime: now() }
      transactions[networkId] = txs
    })
    .addCase(clearAllTransactions, (transactions, { payload: { networkId } }) => {
      if (!transactions[networkId]) return
      transactions[networkId] = {}
    })
    .addCase(checkedTransaction, (transactions, { payload: { networkId, hash, blockNumber } }) => {
      const tx = transactions[networkId]?.[hash]
      if (!tx) {
        return
      }
      if (!tx.lastCheckedBlockNumber) {
        tx.lastCheckedBlockNumber = blockNumber
      } else {
        tx.lastCheckedBlockNumber = Math.max(blockNumber, tx.lastCheckedBlockNumber)
      }
    })
    .addCase(finalizeTransaction, (transactions, { payload: { hash, networkId, receipt } }) => {
      const tx = transactions[networkId]?.[hash]
      if (!tx) {
        return
      }
      tx.receipt = receipt
      tx.confirmedTime = now()
    })
)
